import FormGroup from "@/app/shared/form-group";
import { DatePicker } from "@/components/ui/datepicker";
import QuillEditor from "@/components/ui/quille-editor/quill-editor";
import useProfile from "@/hooks/use-profile";
import useApi from "@/hooks/useApi";
import {
  marketSegmentOptions,
  tenureOptions,
  whatIsNextStatusOptions,
} from "@/utilities/enums/Project";
import { sortSelectOptionsAZ } from "@/utilities/functions";
import CategoryRepository from "@/utilities/repositories/Categories";
import DeveloperRepository from "@/utilities/repositories/Developer.ts";
import LocationRepository from "@/utilities/repositories/Location";
import { IDeveloperFromList } from "@/utilities/types/Developer.ts";
import { ILocation } from "@/utilities/types/Location";
import { EProjectWhatIsNextStatus, ICategory } from "@/utilities/types/Project";
import { IResponseData } from "@/utilities/types/requests";
import { Roles } from "@/utilities/types/Users.ts";
import cn from "@/utils/class-names";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { LoaderIcon } from "react-hot-toast";
import { PiXBold } from "react-icons/pi";
import { Badge, Input, Select, SelectOption, Radio, Switch } from "rizzui";

export interface InformationProjectPartProps {
  className?: string;
}

export default function InformationProjectPart({
  className,
}: InformationProjectPartProps) {
  const {
    request: fetchAllCategories,
    response: categories,
    loading,
  } = useApi<IResponseData<ICategory[]>>({
    request: CategoryRepository.getAllCategories,
  });

  const { request: fetchAllDeveloper, response: developers } = useApi<
    IResponseData<IDeveloperFromList[]>
  >({
    request: DeveloperRepository.getAllDevelopers,
  });

  const {
    request: fetchAllLocation,
    response: locations,
    loading: loadingLocation,
  } = useApi<IResponseData<ILocation[]>>({
    request: LocationRepository.getAllLocation,
  });

  const categoyOptions = sortSelectOptionsAZ(
    categories?.data.map((item: ICategory) => ({
      value: item.id,
      label: item.shortname ?? item.name,
    })) || [],
  );

  const locationOptions = locations?.data.map((item: ILocation) => ({
    value: item.id,
    label: item.name,
  }));

  const developerOptions = sortSelectOptionsAZ(
    developers?.data
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item: IDeveloperFromList) => ({
        value: item.id,
        label: item.name,
      })) || [],
  );

  useEffect(() => {
    Promise.all([
      fetchAllCategories(),
      fetchAllLocation(),
      fetchAllDeveloper(),
    ]);
  }, []);

  const { profile } = useProfile();
  const {
    register,
    watch,
    setValue,
    getFieldState,
    formState: { errors },
    control,
    getValues,
  } = useFormContext();

  const onActiveWhatIsNextStatus = (value: EProjectWhatIsNextStatus) => {
    setValue("whatIsNextStatus", value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };
  const activeOptionValue = watch("whatIsNextStatus");

  // const manualTotalUnitsCount = watch("manualTotalUnitsCount");
  // const currentAvailableUnitsCount = watch("manualAvailableUnitsCount");

  // useEffect(() => {
  //   if (!getFieldState("manualAvailableUnitsCount").isDirty) {
  //     setValue("manualAvailableUnitsCount", manualTotalUnitsCount);
  //   }
  // }, [
  //   setValue,
  //   manualTotalUnitsCount,
  //   getFieldState,
  //   currentAvailableUnitsCount,
  // ]);

  return (
    <FormGroup
      title="Information"
      description="Information of this project"
      className={cn(className)}
    >
      <Input
        label="Name"
        placeholder="Name of the project"
        disabled={profile?.role === Roles.AGENCY}
        {...register("name")}
        error={errors.name?.message as string}
      />
      <Input
        label="Area"
        min={0}
        disabled={profile?.role === Roles.AGENCY}
        type="number"
        placeholder="0.00"
        {...register("area")}
        error={errors.area?.message as string}
      />
      <Input
        label="Slug"
        min={0}
        disabled={profile?.role === Roles.AGENCY}
        type="text"
        placeholder="Slug of the project"
        {...register("slug")}
        value={watch("slug")?.trim()}
        error={errors.area?.message as string}
        className="col-span-full"
      />
      <Controller
        name="tenure"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            dropdownClassName="!z-0"
            options={tenureOptions}
            value={value}
            onChange={onChange}
            disabled={profile?.role === Roles.AGENCY}
            label="Tenure"
            error={errors?.tenure?.message as string}
            getOptionValue={(option) => option.value}
            displayValue={(selected: string) => {
              const selectedOption = tenureOptions?.find(
                (option) => option.value === selected,
              );
              return selectedOption?.label;
            }}
          />
        )}
      />
      <Controller
        name="developerId"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            dropdownClassName="!z-0"
            options={developerOptions}
            value={value}
            disabled={profile?.role === Roles.AGENCY}
            onChange={onChange}
            label="Developer"
            error={errors?.developerId?.message as string}
            getOptionValue={(option) => option.value}
            displayValue={(selected: string) => {
              const selectedOption = developerOptions?.find(
                (option) => option.value === selected,
              );
              return selectedOption?.label;
            }}
            searchable={true}
          />
        )}
      />
      <Controller
        name="marketSegment"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            dropdownClassName="!z-0"
            options={marketSegmentOptions}
            value={value}
            disabled={profile?.role === Roles.AGENCY}
            onChange={onChange}
            label="Market Segment"
            error={errors?.marketSegment?.message as string}
            getOptionValue={(option) => option.value}
            displayValue={(selected: string) => {
              const selectedOption = marketSegmentOptions?.find(
                (option) => option.value === selected,
              );
              return selectedOption?.label;
            }}
          />
        )}
      />
      <Controller
        name="categoryId"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            options={categoyOptions ?? []}
            value={value}
            disabled={profile?.role === Roles.AGENCY}
            onChange={onChange}
            label="Category"
            error={errors?.categoryId?.message as string}
            getOptionValue={(option) => option.value}
            inPortal={false}
            suffix={loading ? <LoaderIcon /> : undefined}
            displayValue={(selected: string) => {
              const selectedOption = categoyOptions?.find(
                (option) => option.value === selected,
              );
              return selectedOption?.label;
            }}
          />
        )}
      />
      <Controller
        name="locationId"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            options={locationOptions ?? []}
            value={value}
            onChange={onChange}
            label="District"
            disabled={profile?.role === Roles.AGENCY}
            error={errors?.locationId?.message as string}
            getOptionValue={(option) => option.value}
            inPortal={false}
            suffix={loadingLocation ? <LoaderIcon /> : undefined}
            displayValue={(selected: string) => {
              const selectedOption = locationOptions?.find(
                (option) => option.value === selected,
              );
              return selectedOption?.label;
            }}
          />
        )}
      />
      <Controller
        name="whatIsNextStatus"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            options={whatIsNextStatusOptions ?? []}
            value={value}
            onChange={onChange}
            label="Project Stage"
            disabled={profile?.role === Roles.AGENCY}
            error={errors?.whatIsNextStatus?.message as string}
            getOptionValue={(option) => option.value}
            inPortal={false}
            suffix={loadingLocation ? <LoaderIcon /> : undefined}
            displayValue={(selected: EProjectWhatIsNextStatus) => {
              return <p className="capitalize">{selected}</p>;
            }}
            optionClassName="w-full [&>div]:w-full"
            getOptionDisplayValue={(option: SelectOption) => (
              <TemplateWhatIsNextOption
                option={option}
                active={activeOptionValue === option.value}
                onActive={onActiveWhatIsNextStatus}
              />
            )}
          />
        )}
      />
      <div className="[&>.react-datepicker-wrapper]:w-full">
        <Controller
          name="tenureEffectFrom"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="relative">
              <DatePicker
                disabled={profile?.role === Roles.AGENCY}
                inputProps={{
                  label: "Tenure Effect From",
                  error: errors?.tenureEffectFrom?.message as string,
                }}
                placeholderText="Select Date"
                selected={value}
                onChange={onChange}
              />
              {value && profile?.role !== Roles.AGENCY && (
                <button
                  type="button"
                  className="absolute top-8 right-10 translate-y-1/3 text-gray-400 hover:text-gray-600 h-4 w-4"
                  onClick={() => onChange(null)}
                >
                  <PiXBold className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        />
      </div>
      <div className="[&>.react-datepicker-wrapper]:w-full">
        <Controller
          name="expectedTop"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              disabled={profile?.role === Roles.AGENCY}
              inputProps={{
                label: "TOP",
                error: errors?.expectedTop?.message as string,
              }}
              placeholderText="Select Date"
              selected={value}
              onChange={onChange}
              dateFormat="MMM, yyyy"
              showMonthYearPicker
            />
          )}
        />
      </div>
      <Input
        label="Target Preview Launch Date (Month)"
        placeholder="Mar/April"
        disabled={profile?.role === Roles.AGENCY}
        {...register("targetPreviewLaunchDateMonth")}
        error={errors.targetPreviewLaunchDateMonth?.message as string}
      />
      <div className="[&>.react-datepicker-wrapper]:w-full">
        <Controller
          name="targetPreviewLaunchDateYear"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              disabled={profile?.role === Roles.AGENCY}
              inputProps={{
                label: "Target Preview Launch Date (Year)",
                error: errors?.targetPreviewLaunchDateYear?.message as string,
              }}
              placeholderText="Select Year"
              selected={
                value ? new Date(parseInt(value as string), 0, 1) : null
              }
              onChange={(date: Date) => onChange(date?.getFullYear())}
              showYearPicker
              dateFormat="yyyy"
              yearItemNumber={10}
              minDate={new Date()}
            />
          )}
        />
      </div>
      <div className="[&>.react-datepicker-wrapper]:w-full">
        <Input
          label="Direct Commission"
          min={0}
          disabled={profile?.role === Roles.AGENCY}
          type="number"
          placeholder="0.00"
          {...register("directCommission")}
          error={errors.directCommission?.message as string}
        />
      </div>
      <Input
        label="Total units"
        min={0}
        disabled={profile?.role === Roles.AGENCY}
        type="number"
        placeholder=""
        {...register("manualTotalUnitsCount")}
        error={errors.area?.message as string}
      />
      <Input
        label="Available units"
        min={0}
        disabled={profile?.role === Roles.AGENCY}
        type="number"
        placeholder=""
        {...register("manualAvailableUnitsCount")}
        error={errors.area?.message as string}
      />
      <Input
        label="Address"
        className="col-span-full"
        placeholder="Address"
        disabled={profile?.role === Roles.AGENCY}
        {...register("address")}
        error={errors.address?.message as string}
      />
      <div>
        <span className="text-sm font-medium">Showflat Location</span>
        <Controller
          name="isShowflatLocation"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Switch
              label="Enable"
              size="lg"
              className=""
              disabled={false}
              checked={!!field?.value}
              error={error?.message}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      {watch("isShowflatLocation") && (
        <Input
          label="Showflat Address"
          className="col-span-full"
          placeholder="Showflat Address"
          disabled={profile?.role === Roles.AGENCY}
          {...register("showflatLocation")}
          error={errors.address?.message as string}
        />
      )}
      <Controller
        control={control}
        name="description"
        render={({ field: { onChange, value } }) => (
          <QuillEditor
            value={value}
            onChange={onChange}
            readOnly={profile?.role === Roles.AGENCY}
            label="Description"
            className="col-span-full [&_.ql-editor]:min-h-[100px]"
            labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
            error={errors?.description?.message as string}
          />
        )}
      />
      <Controller
        control={control}
        name="facilityHtml"
        render={({ field: { onChange, value } }) => (
          <QuillEditor
            value={value}
            onChange={onChange}
            readOnly={profile?.role === Roles.AGENCY}
            label="Facility"
            className="col-span-full [&_.ql-editor]:min-h-[100px]"
            labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
            error={errors?.facilityHtml?.message as string}
          />
        )}
      />
      <Input
        label="Google Map Url"
        className="col-span-full"
        placeholder="Google Map Url"
        disabled={profile?.role === Roles.AGENCY}
        {...register("googleMapUrl")}
        error={errors.googleMapUrl?.message as string}
      />
    </FormGroup>
  );
}

const TemplateWhatIsNextOption = ({
  option,
  active,
  onActive,
}: {
  option: SelectOption;
  active: boolean;
  onActive: (value: EProjectWhatIsNextStatus) => void;
}) => {
  return (
    <div className="flex flex-row items-center justify-between w-full">
      <div className="flex-1 flex flex-row items-center min-w-max">
        <span className="ml-2">{option?.label}</span>
      </div>
      <div className="flex flex-row items-center min-w-max gap-2">
        {active && (
          <Badge size="sm" color="success">
            Active
          </Badge>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onActive(option?.value as EProjectWhatIsNextStatus);
          }}
        >
          <Radio
            size="sm"
            checked={active}
            readOnly
            className="pointer-events-none [&_svg]:top-0"
          />
        </button>
      </div>
    </div>
  );
};
